import React from 'react'
import styled from '@emotion/styled'
import { Container } from 'components/layout/Container/Container'
import { PAGE_BREAKPOINTS } from 'parameters/index'
import { ArticleSummary } from 'services/ContentfulService/models/articles'
import { ArticleGrid } from 'experiences/learn/articles/ArticleGrid'
import { useAuthState } from 'services/Authentication'
import { useHasMounted } from 'lib/renderWrappers/ClientOnly'
import { AdvertiserDisclosure } from 'components/content'

interface Props {
	articles: ArticleSummary[]
	children: React.ReactNode
	title?: string
	description?: string
	background?: string
	hideAdvertiserDisclosure?: boolean
}

const ExplorePageLayout = ({
	articles,
	children,
	title,
	description,
	background,
	hideAdvertiserDisclosure = false
}: Props): JSX.Element => {
	const { userType, profile } = useAuthState()
	const hasMounted = useHasMounted()

	const renderTitle = (titleValue?: string): string | undefined => {
		if (userType === 'loggedIn' && hasMounted) {
			return `${profile?.displayName}, ${titleValue?.toLowerCase()}`
		}
		return titleValue
	}

	return (
		<div>
			<ExplorePageContainer background={background}>
				<ExplorePageTop>
					{title && <h1>{renderTitle(title)}</h1>}
					{description && (
						<SubTitleWrapper>
							<SubTitle>{description}</SubTitle>
							{hideAdvertiserDisclosure ? null : <AdvertiserDisclosure />}
						</SubTitleWrapper>
					)}
				</ExplorePageTop>
				<WidgetWrapper>{children}</WidgetWrapper>
				{articles.length > 0 && <ArticleGrid articles={articles} />}
			</ExplorePageContainer>
		</div>
	)
}

export default ExplorePageLayout

const ExplorePageContainer = styled(Container)<{ background?: string }>`
	background: ${({ background }): string => (background ? `url(${background}) center right no-repeat` : 'none')};
	background-position: 98% 2%;
	padding: 20px 0;

	@media screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		padding: 10px;
	}
`

export const ExplorePageTop = styled.div`
	padding-bottom: 60px;

	@media screen and (${PAGE_BREAKPOINTS.MOBILE}) {
		padding-bottom: 40px;
	}
`

const SubTitleWrapper = styled.div`
	margin-top: 16px;
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	@media screen and (${PAGE_BREAKPOINTS.TABLET_PORTRAIT}) {
		flex-direction: column;
		align-items: flex-start;
		> div {
			margin-left: auto;
			margin-top: 8px;
		}
	}
`

const SubTitle = styled.span``

const WidgetWrapper = styled.div`
	margin-bottom: 32px;

	iframe {
		margin-top: -20px;
	}
`
